/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable import/prefer-default-export */
import React from "react";
import Grid from "@mui/material/Grid";
import { useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { Box, Button, TextField, Typography, MenuItem } from "@mui/material";

import { RootState } from "../../../../../store";
import Panel from "../../../../../components/Panel";
import { tl } from "../../../../../components/translate";
import { ShiftStatus } from "../../../../../constants/shift";
import { IShift } from "../../../../../interfaces/ShiftInterface";
import { usePostShift, usePatchShift } from "../../../../../hooks/query/useShifts";

interface ShiftCreateEditProps {
  onClose: () => void;
  shift?: Partial<IShift> | null;
  onSuccess: (data?: IShift) => void;
}

const parseTimeToMinutes = (time?: string): number | undefined => {
  if (!time) return undefined;

  const [hour, minute] = time.split(":").map(Number);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return undefined;

  return hour * 60 + minute;
};

const getShiftLengthHours = (start?: string, end?: string): string => {
  const startTotal = parseTimeToMinutes(start);
  const endTotalRaw = parseTimeToMinutes(end);
  if (startTotal === undefined || endTotalRaw === undefined) return "";

  let endTotal = endTotalRaw;
  if (endTotal < startTotal) {
    endTotal += 24 * 60;
  }

  const totalMinutes = endTotal - startTotal;
  const hours = totalMinutes / 60;
  return `${hours.toFixed(2)} hours`;
};

export function ShiftCreateEdit({
  onClose,
  shift,
  onSuccess
}: ShiftCreateEditProps): React.ReactNode {
  const isEdit = !!shift?.id;
  const rcId = useSelector((state: RootState) => state.userContext.resourceCentre?.id);

  const { mutate: postShiftMutation } = usePostShift();
  const { mutate: patchShiftMutation } = usePatchShift();

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting }
  } = useForm<IShift>({
    defaultValues: {
      name: shift?.name || "",
      shiftCode: shift?.shiftCode || "",
      startTime: shift?.startTime || "09:00",
      endTime: shift?.endTime || "17:00",
      graceInMinutes: shift?.graceInMinutes || 0,
      graceOutMinutes: shift?.graceOutMinutes || 0,
      status: shift?.status || ShiftStatus.ACTIVE,
      breakStartTime: shift?.breakStartTime || "12:00",
      breakEndTime: shift?.breakEndTime || "12:30",
      resourceCentreId: rcId || 0
    }
  });

  const startTime = watch("startTime");
  const endTime = watch("endTime");

  const shiftLengthInfo = getShiftLengthHours(startTime, endTime);
  const startMinutes = parseTimeToMinutes(startTime);
  const endMinutes = parseTimeToMinutes(endTime);
  const isOvernightInfo =
    startMinutes !== undefined && endMinutes !== undefined && endMinutes < startMinutes
      ? "Yes"
      : "No";

  const onSubmit = async (data: IShift) => {
    if (!rcId) return;
    const payload: Partial<IShift> = {
      ...data,
      resourceCentreId: rcId
    };

    if (isEdit && shift?.id) {
      patchShiftMutation(
        { id: shift.id, data: payload },
        {
          onSuccess: (response) => {
            if (response.success) {
              onSuccess(response.data);
              onClose();
            }
          }
        }
      );
    } else {
      postShiftMutation(payload, {
        onSuccess: (response) => {
          if (response.success) {
            onSuccess(response.data);
            onClose();
          }
        }
      });
    }
  };

  const footer = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        width: "100%",
        justifyContent: "flex-end",
        gap: 2
      }}
    >
      <Button onClick={onClose}>{tl("cancel")}</Button>
      <Button type="submit" variant="contained" form="shift-form" disabled={isSubmitting}>
        {isEdit ? tl("update") : tl("create")}
      </Button>
    </Box>
  );

  return (
    <Panel title={isEdit ? "Edit Shift" : "Create Shift"} onClose={onClose} footer={footer}>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <form id="shift-form" onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="name"
                control={control}
                rules={{ required: "Name is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="outlined"
                    label="Shift Name"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    onFocus={(event) => event.target.select()}
                    slotProps={{
                      inputLabel: { shrink: true }
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="shiftCode"
                control={control}
                rules={{ required: "Code is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="outlined"
                    label="Shift Code"
                    error={!!errors.shiftCode}
                    helperText={errors.shiftCode?.message}
                    onFocus={(event) => event.target.select()}
                    slotProps={{
                      inputLabel: { shrink: true }
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="startTime"
                control={control}
                rules={{ required: "Start time is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="outlined"
                    label="Start Time"
                    type="time"
                    error={!!errors.startTime}
                    helperText={errors.startTime?.message}
                    onFocus={(event) => event.target.select()}
                    slotProps={{
                      inputLabel: { shrink: true }
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="endTime"
                control={control}
                rules={{ required: "End time is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="outlined"
                    label="End Time"
                    type="time"
                    error={!!errors.endTime}
                    helperText={errors.endTime?.message}
                    onFocus={(event) => event.target.select()}
                    slotProps={{
                      inputLabel: { shrink: true }
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="breakStartTime"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="outlined"
                    label="Break Start Time"
                    type="time"
                    error={!!errors.breakStartTime}
                    helperText={errors.breakStartTime?.message}
                    onFocus={(event) => event.target.select()}
                    slotProps={{
                      inputLabel: { shrink: true }
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="breakEndTime"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="outlined"
                    label="Break End Time"
                    type="time"
                    error={!!errors.breakEndTime}
                    helperText={errors.breakEndTime?.message}
                    onFocus={(event) => event.target.select()}
                    slotProps={{
                      inputLabel: { shrink: true }
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="graceInMinutes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="outlined"
                    label="Grace In (Minutes)"
                    type="number"
                    error={!!errors.graceInMinutes}
                    helperText={errors.graceInMinutes?.message}
                    onFocus={(event) => event.target.select()}
                    slotProps={{
                      inputLabel: { shrink: true }
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="graceOutMinutes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="outlined"
                    label="Grace Out (Minutes)"
                    type="number"
                    error={!!errors.graceOutMinutes}
                    helperText={errors.graceOutMinutes?.message}
                    onFocus={(event) => event.target.select()}
                    slotProps={{
                      inputLabel: { shrink: true }
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    variant="outlined"
                    label="Status"
                    error={!!errors.status}
                    helperText={errors.status?.message}
                    slotProps={{
                      inputLabel: { shrink: true }
                    }}
                  >
                    <MenuItem value={ShiftStatus.ACTIVE}>Active</MenuItem>
                    <MenuItem value={ShiftStatus.INACTIVE}>Inactive</MenuItem>
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={12}>
              <Typography variant="body2" sx={{ mt: 1, mb: 0.5 }}>
                Is Shift Overnight: {isOvernightInfo}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Shift Length: {shiftLengthInfo}
              </Typography>
            </Grid>
          </Grid>
        </form>
      </Box>
    </Panel>
  );
}
